import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useUnitLinks } from 'core/contexts/unitLinks';
import { useExperiences } from 'core/contexts/experienceV2';
import { useTranslation } from 'react-i18next';
import Layout from 'components/Layout';
import { Section } from 'modules/Pricing/styles';
import { CircularIndeterminate } from '@utriper/core-ui';
import { Button, Grid2, IconButton, Typography, useMediaQuery } from '@mui/material';
import { Add } from '@mui/icons-material';
import FiltersForm from './components/FiltersForm';
import UnitCards from './components/UnitCards';
import UnitLinkForm from 'components/UnitLinkForm';
import NoData from './components/NoData';

const ExperienceUnitsPage = () => {
    const { id } = useParams();
    const experienceId = parseInt(id);
    const { t } = useTranslation();
    const translate = (key) => t(`experience_units.${key}`);
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const { getExperienceById, loading, experience } = useExperiences();
    const { getExperienceLinks, loadingExpLink, expLinks, expLinkFilters, expNameFilterDebounce, expLinkPage, addExperienceLink, submittingExpLink } = useUnitLinks();
    const [openForm, setOpenForm] = useState(false);

    useEffect(() => {
        getExperienceById(experienceId, history);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [experienceId]);

    useEffect(() => {
        getExperienceLinks(experienceId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [experienceId, expLinkPage, expNameFilterDebounce, expLinkFilters]);

    const handleOpenForm = (newValue = true) => setOpenForm(newValue);

    const handleAddExperienceLink = async (values) => {
        await addExperienceLink(experienceId, values);
        handleOpenForm(false);
    };

    return (
        <Layout>
            {loading ? (
                <CircularIndeterminate centered />
            ) : (
                <Section style={{ overflow: 'visible' }}>
                    {/* Header */}
                    <Grid2 container size={12} justifyContent="space-between" alignItems="center">
                        <Grid2
                            container
                            size={10}
                            direction="column"
                            justifyContent="flex-start"
                            alignItems="flex-start"
                            gap="8px"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {/* Title */}
                            <Typography component="h1" fontSize={20} fontWeight={900} sx={{ color: '#131313', maxWidth: '100%' }} noWrap>
                                {experience?.name}
                            </Typography>
                            <Typography component="p" fontSize={16} fontWeight={500} sx={{ color: '#131313bf', whiteSpace: 'break-spaces' }}>
                                {translate('subtitle')}
                            </Typography>
                        </Grid2>

                        <Grid2 container size={2} justifyContent="flex-end">
                            {isMobile ? (
                                <IconButton
                                    onClick={handleOpenForm}
                                    sx={{
                                        backgroundColor: 'var(--color-utriper-blue)',
                                        color: '#fff',
                                        '&:hover': {
                                            backgroundColor: 'var(--color-utriper-blue-dark)',
                                        },
                                        borderRadius: '4px',
                                    }}
                                >
                                    <Add />
                                </IconButton>
                            ) : (
                                <Button
                                    sx={{
                                        backgroundColor: 'var(--color-utriper-blue)',
                                        padding: '12px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        '&:hover': {
                                            backgroundColor: 'var(--color-utriper-blue-dark)',
                                        },
                                    }}
                                    variant="contained"
                                    startIcon={<Add sx={{ color: '#fff', fontSize: '24px' }} />}
                                    onClick={handleOpenForm}
                                >
                                    {translate('add_button')}
                                </Button>
                            )}
                        </Grid2>
                    </Grid2>

                    {/* Filters */}
                    <FiltersForm />

                    {/* Cards */}
                    {loadingExpLink ? <CircularIndeterminate /> : expLinks?.length > 0 ? <UnitCards experienceId={experienceId} /> : <NoData handleOpenForm={handleOpenForm} />}

                    {/* Add Form */}
                    <UnitLinkForm onSubmit={handleAddExperienceLink} submitting={submittingExpLink} open={openForm} onClose={() => handleOpenForm(false)} unitsCreated={expLinks || []} />
                </Section>
            )}
        </Layout>
    );
};

export default ExperienceUnitsPage;
